import type Flickity from "flickity";
import { Immutable } from "immer";
import React from "react";
import { useFlickity } from "tsi-common-react/src/hooks/useFlickity";

import type { Carousel as Value } from "@reactivated";

import { EditorContent } from "@thelabnyc/thelabui/src/components/EditorContent";
import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import { getLinkUrl, originalUrl } from "../../utils/wagtail";

import styles from "./index.module.scss";

type IProps = Immutable<{
    value: Value;
}>;

const slideCountClassNames: Partial<Record<number, string>> = {
    3: styles.carouselX3,
    4: styles.carouselX4,
};

const tabCellClassNames = [
    styles.carouselTabCell1,
    styles.carouselTabCell2,
    styles.carouselTabCell3,
    styles.carouselTabCell4,
];

type ISlide = NonNullable<Immutable<Value>["slides"]>[number];
type ISlideChild = NonNullable<ISlide["body"]>[number];

const renderSlideChild = (block: ISlideChild): React.JSX.Element => {
    switch (block.type) {
        case "subtitle":
            return (
                <h4 className={styles.carouselCellHeadline} key={block.id}>
                    {block.value}
                </h4>
            );
        case "description":
            return <p key={block.id}>{block.value}</p>;
        case "price":
            return (
                <p className={styles.carouselCellPrice} key={block.id}>
                    {block.value}
                </p>
            );
        case "cta":
            // The legacy carousel_cta.html hardcoded title="#"; preserved.
            return (
                <a
                    key={block.id}
                    className="button"
                    href={getLinkUrl(block.value)}
                    title="#"
                >
                    {block.value.display_name}
                </a>
            );
    }
};

/**
 * The Carousel drives Flickity over the cells and syncs the tab buttons to
 * the selected cell via ``activeTab`` state (``active`` +
 * `aria-current`); clicking a tab selects its cell. Flickity
 * restructures the cells subtree imperatively after mount, but the cells' JSX
 * is stable across renders, so React never reconciles into that subtree.
 *
 * - ``slide.title`` is a RawHTMLBlock, so it is injected via
 *   ``dangerouslySetInnerHTML``.
 */
export const Carousel = (props: IProps): React.JSX.Element => {
    const slides = props.value.slides ?? [];
    const cellsRef = React.useRef<HTMLDivElement>(null);
    const [activeTab, setActiveTab] = React.useState(0);
    const handleFlickityInit = React.useCallback(
        (flickity: Flickity): (() => void) => {
            const handleSelect = (): void => {
                setActiveTab(flickity.selectedIndex);
            };
            flickity.on("select", handleSelect);
            return () => {
                flickity.off("select", handleSelect);
            };
        },
        [],
    );
    const { flickityRef } = useFlickity(cellsRef, {
        options: { prevNextButtons: true, pageDots: false },
        enabled: slides.length > 0,
        deps: [slides.length],
        onInit: handleFlickityInit,
    });
    return (
        <section
            className={concatClassNames([
                styles.carousel,
                slideCountClassNames[slides.length],
            ])}
            aria-label="carousel"
        >
            <div className={styles.carouselContainer}>
                <h3 className={styles.carouselHeadline}>{props.value.title}</h3>
                <div className={styles.carouselTabs}>
                    {slides.map((slide, index) => (
                        <EditorContent key={index}>
                            {(marker) => (
                                <button
                                    className={concatClassNames([
                                        styles.carouselTab,
                                        tabCellClassNames[index],
                                        index === activeTab ? "active" : null,
                                    ])}
                                    aria-current={
                                        index === activeTab ? "true" : "false"
                                    }
                                    onClick={() =>
                                        flickityRef.current?.select(index)
                                    }
                                    dangerouslySetInnerHTML={{
                                        __html: slide.title ?? "",
                                    }}
                                    {...marker}
                                />
                            )}
                        </EditorContent>
                    ))}
                    <hr />
                </div>
                <div className={styles.carouselCells} ref={cellsRef}>
                    {slides.map((slide, index) => (
                        <div key={index} className={styles.carouselCell}>
                            <div className={styles.carouselCellItem}>
                                <figure className={styles.carouselCellImage}>
                                    <img
                                        className="img-responsive"
                                        src={originalUrl(slide.image)}
                                        alt={
                                            slide.image?.default_alt_text ?? ""
                                        }
                                        loading="lazy"
                                        decoding="async"
                                    />
                                </figure>
                                {slide.body && slide.body.length > 0 && (
                                    <div className={styles.carouselCellCopy}>
                                        {slide.body.map(renderSlideChild)}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
};
